const SECOND = 1_000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/**
 * Locale for date and currency rendering.
 *
 * Module-level rather than threaded through all 14 call sites, and mutable
 * rather than a hook, so that adding Malayalam did not require touching every
 * caller in the app. `I18nProvider` sets it whenever the signed-in user
 * changes; a re-render always follows that, so no component can read a stale
 * value for longer than the paint that triggered the change.
 *
 * ONLY dates are localised. Currency is deliberately pinned to `en-IN`.
 *
 * That is not a simplification, it is a correctness requirement, and getting it
 * wrong is how this nearly shipped a money bug. `ml-IN` does NOT use Indian
 * lakh grouping:
 *
 *     amount     en-IN        ml-IN
 *     99,999     ₹99,999      ₹99,999      (agree)
 *     1,52,500   ₹1,52,500    ₹152,500     (DIVERGE)
 *     12,50,000  ₹12,50,000   ₹1,250,000   (DIVERGE)
 *
 * The two agree only below ₹1,00,000, which is why a single spot-check at
 * ₹1,525 "proved" they were identical. A mis-grouped rupee figure on a fee
 * demand does not read as a formatting nit to an Indian parent — it reads as a
 * different amount. So `formatINR` stays `en-IN` in both languages, and a
 * regression test pins the divergent magnitudes.
 */
type FormatLocale = 'en-IN' | 'ml-IN';

let activeLocale: FormatLocale = 'en-IN';

/** Called by `I18nProvider`. Not for component use. */
export const setFormatLocale = (locale: 'en' | 'ml'): void => {
  activeLocale = locale === 'ml' ? 'ml-IN' : 'en-IN';
};

/**
 * Formatters are cached per locale. `Intl.DateTimeFormat` construction is not
 * free and these run inside virtualised table rows, so building one per call
 * would be a real cost on the list screens.
 */
const cache = new Map<
  string,
  Intl.DateTimeFormat | Intl.NumberFormat | Intl.RelativeTimeFormat
>();

const cached = <
  T extends Intl.DateTimeFormat | Intl.NumberFormat | Intl.RelativeTimeFormat,
>(
  kind: string,
  build: (locale: FormatLocale) => T,
): T => {
  const key = `${kind}:${activeLocale}`;
  const existing = cache.get(key);
  if (existing !== undefined) return existing as T;
  const made = build(activeLocale);
  cache.set(key, made);
  return made;
};

const dateOnlyFmt = () =>
  cached('dateOnly', (locale) =>
    new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: '2-digit' }),
  );

const dateWithTimeFmt = () =>
  cached('dateWithTime', (locale) =>
    new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }),
  );

/**
 * `'en-IN'` hardcoded, NOT `activeLocale`. See the note at the top of this file:
 * ml-IN switches to Western digit grouping above ₹1,00,000.
 */
const inrFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const inrFormatterCompact = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const inrFmt = () => inrFormatter;
const inrCompactFmt = () => inrFormatterCompact;

const toDate = (iso: string): Date | null => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

const relativeFmt = () =>
  cached('relative', (locale) =>
    // `numeric: 'auto'` so English says "yesterday" rather than "1 day ago",
    // and Malayalam gets whatever its own convention is — the point of using
    // the platform formatter instead of hand-writing suffixes per language.
    new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }),
  );

/**
 * Human-readable "how long ago", localised.
 *
 * Previously this returned hardcoded English (`'just now'`, `` `${d}d ago` ``)
 * for everything under a week and localised only its over-a-week fallback —
 * which is backwards, since under a week is the common case. A Malayalam parent
 * read "അവസാന കുറിപ്പ്: … · 3d ago": one sentence, two languages, on the one
 * page that had been translated. `Intl.RelativeTimeFormat` removes the need for
 * dictionary keys here entirely.
 */
export const timeAgo = (iso: string): string => {
  const d = toDate(iso);
  if (!d) return '';
  const diff = Date.now() - d.getTime();
  const rtf = relativeFmt();
  // Clamp future timestamps to "now": a clock skew between the Worker and the
  // browser should not render "in 3 minutes" on an audit trail.
  if (diff < MINUTE) return rtf.format(0, 'second');
  if (diff < HOUR) return rtf.format(-Math.floor(diff / MINUTE), 'minute');
  if (diff < DAY) return rtf.format(-Math.floor(diff / HOUR), 'hour');
  if (diff < WEEK) return rtf.format(-Math.floor(diff / DAY), 'day');
  return dateOnlyFmt().format(d);
};

/**
 * Format a paise value as Indian Rupees. By default decimals are hidden when
 * the amount is whole rupees (the wallet/referrals UI always uses round
 * rupees, so this is the cleaner default). Pass `{ alwaysShowPaise: true }`
 * when the source value can carry fractional rupees (e.g. raw ledger entries
 * imported from an external payments processor).
 *
 * Negative values render with a leading minus from `Intl.NumberFormat`; the
 * caller is responsible for any explicit `+` sign on credits if desired.
 */
export const formatINR = (
  paise: number,
  opts?: { alwaysShowPaise?: boolean },
): string => {
  const rupees = paise / 100;
  if (!opts?.alwaysShowPaise && Number.isInteger(rupees)) {
    return inrCompactFmt().format(rupees);
  }
  return inrFmt().format(rupees);
};

/**
 * Render a *signed* paise amount. Positive values get a `+` prefix so credits
 * and debits read at a glance in the wallet ledger.
 */
export const formatINRSigned = (paise: number): string => {
  if (paise > 0) return `+${formatINR(paise)}`;
  // Intl already adds the minus sign for negatives.
  return formatINR(paise);
};

export const formatDate = (iso: string, opts?: { withTime?: boolean }): string => {
  const d = toDate(iso);
  if (!d) return '';
  return opts?.withTime ? dateWithTimeFmt().format(d) : dateOnlyFmt().format(d);
};
